<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>登录态管理</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 登录态列表卡片 -->
    <el-card class="search-card">
      <div slot="header" class="toolbar">
        <span class="card-header">登录态列表</span>
        <el-button type="primary" @click="handleAdd">添加登录态</el-button>
      </div>
      <div class="toolbar-tip" style="margin-bottom: 12px;">采集登录凭据（美团管家/订货通=供应链采集，美团外卖商家版=巡店助手播报），保存时服务端强制验活，多人上传时以有效期最长的凭据为准</div>

      <el-table :data="list" border stripe v-loading="listLoading">
        <el-table-column label="平台" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="platformTagType(scope.row.platform)" size="small">
              {{ platformLabel(scope.row.platform) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" min-width="130" show-overflow-tooltip />
        <el-table-column label="过期时间" width="160" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.expireAt">{{ $getDateTime(scope.row.expireAt) }}</span>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="剩余有效期" width="110" align="center">
          <template slot-scope="scope">
            <el-tag :type="expireTagType(scope.row)" size="small">{{ expireTagText(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="来源" width="80" align="center">
          <template slot-scope="scope">
            {{ scope.row.source === 'plugin' ? '插件' : '手工' }}
          </template>
        </el-table-column>
        <el-table-column label="最近验活" width="160" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.lastValidatedAt">{{ $getDateTime(scope.row.lastValidatedAt) }}</span>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
        <el-table-column label="更新人/时间" width="180" align="center">
          <template slot-scope="scope">
            <div>{{ scope.row.updatedBy || '—' }}</div>
            <div class="sub-text" v-if="scope.row.updatedAt">{{ $getDateTime(scope.row.updatedAt) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleView(scope.row)">查看</el-button>
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="handlePluginConfig(scope.row)">插件配置</el-button>
            <el-button type="text" size="small" style="color:#F56C6C" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 密钥管理卡片 -->
    <el-card class="search-card">
      <div slot="header" class="toolbar">
        <span class="card-header">插件通讯密钥</span>
      </div>
      <el-form label-width="120px" size="medium" @submit.native.prevent>
        <el-form-item label="当前密钥：">
          <div class="secret-row">
            <el-input
              v-model="secretForm.secret"
              type="password"
              show-password
              placeholder="尚未设置密钥"
              style="max-width: 480px;"
            ></el-input>
            <el-button size="medium" @click="copySecret" :disabled="!secretForm.secret">复制</el-button>
            <el-button type="primary" size="medium" :loading="secretSaving" @click="saveSecret(true)" :disabled="!secretDirty">保存修改</el-button>
            <el-button type="warning" size="medium" :loading="secretGenerating" @click="saveSecret(false)">一键生成</el-button>
          </div>
        </el-form-item>
      </el-form>
      <div class="secret-tip">
        用于浏览器插件向后台上传登录态时的通讯鉴权，请妥善保管；重新生成后旧密钥立即失效，需同步更新插件配置。
      </div>
    </el-card>

    <!-- 添加/编辑弹窗 -->
    <el-dialog :title="isEdit ? '编辑登录态' : '添加登录态'" :visible.sync="dialogVisible" width="720px" @close="resetForm" :close-on-click-modal="false">
      <el-form :model="form" :rules="formRules" ref="formRef" label-width="100px">
        <el-form-item label="平台：" prop="platform">
          <el-select v-model="form.platform" placeholder="选择平台" style="width: 240px;" :disabled="isEdit" @change="onPlatformChange">
            <el-option label="美团管家" value="meituan"></el-option>
            <el-option label="订货通" value="dinghuotong"></el-option>
            <el-option label="美团外卖商家版" value="waimaie"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="名称：" prop="name">
          <el-input v-model="form.name" placeholder="如：配送单采集账号" maxlength="30" style="width: 360px;"></el-input>
        </el-form-item>
        <el-form-item label="备注：">
          <el-input v-model="form.remark" placeholder="选填" maxlength="100" style="width: 360px;"></el-input>
        </el-form-item>
        <el-form-item label="登录态内容：">
          <el-input
            v-model="form.contentText"
            type="textarea"
            :rows="10"
            :placeholder="contentPlaceholder"
          ></el-input>
          <div class="sub-text">可留空：创建「待激活」记录，生成插件配置分发给员工，首次上传后自动激活</div>
        </el-form-item>
        <el-form-item v-if="showPendingBrand" label="品牌ID：" required>
          <el-input v-model="form.pendingBrandId" placeholder="订货通 brandId（必填，插件任务配置的品牌常量依赖它）" style="width: 360px;"></el-input>
        </el-form-item>
        <el-form-item v-if="showPendingBrand" label="品牌名称：">
          <el-input v-model="form.pendingBrandName" placeholder="选填，如：伊兴楼泡馍" style="width: 360px;"></el-input>
        </el-form-item>
        <el-form-item label="过期时间：">
          <div v-if="parseResult.error" class="parse-error">{{ parseResult.error }}</div>
          <div v-else-if="parseResult.expireAt" class="parse-ok">
            {{ $getDateTime(parseResult.expireAt) }}
            <el-tag :type="parseDaysTagType" size="small" style="margin-left: 8px;">剩 {{ parseResult.daysLeft }} 天</el-tag>
          </div>
          <div v-else-if="!(form.contentText || '').trim()" class="sub-text">内容留空 → 创建待激活记录（无过期时间，等插件首传）</div>
          <div v-else class="sub-text">粘贴登录态内容后自动解析</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">{{ submitting ? '验活中…' : (isEdit ? '保存' : '添加') }}</el-button>
      </div>
    </el-dialog>

    <!-- 查看弹窗 -->
    <el-dialog title="登录态详情" :visible.sync="viewVisible" width="720px">
      <el-descriptions :column="2" border size="small" style="margin-bottom: 12px;">
        <el-descriptions-item label="平台">{{ platformLabel(viewData.platform) }}</el-descriptions-item>
        <el-descriptions-item label="名称">{{ viewData.name }}</el-descriptions-item>
        <el-descriptions-item label="过期时间">
          <span v-if="viewData.expireAt">{{ $getDateTime(viewData.expireAt) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="剩余有效期">
          <el-tag :type="expireTagType(viewData)" size="small">{{ expireTagText(viewData) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="来源">{{ viewData.source === 'plugin' ? '插件' : '手工' }}</el-descriptions-item>
        <el-descriptions-item label="最近验活">
          <span v-if="viewData.lastValidatedAt">{{ $getDateTime(viewData.lastValidatedAt) }}</span>
        </el-descriptions-item>
      </el-descriptions>
      <el-input type="textarea" :rows="14" readonly v-model="viewData.contentText"></el-input>
    </el-dialog>

    <!-- 插件配置弹窗 -->
    <el-dialog :title="`插件配置 - ${pluginRow ? platformLabel(pluginRow.platform) + ' / ' + pluginRow.name : ''}`" :visible.sync="pluginVisible" width="720px" :close-on-click-modal="false">
      <el-form label-width="100px" @submit.native.prevent>
        <el-form-item label="API 地址：">
          <div class="plugin-url-row">
            <el-input v-model="pluginBaseUrl" placeholder="https://www.cy10000.cn" style="max-width: 440px;" @change="fetchPluginConfig"></el-input>
            <el-button size="medium" :loading="pluginLoading" @click="fetchPluginConfig">刷新配置</el-button>
          </div>
          <div class="sub-text">生成配置中的上传接口以此地址为基础，需员工浏览器可达（生产默认与本站同域）</div>
        </el-form-item>
        <el-form-item label="上传令牌：">
          <el-tag v-if="pluginHasToken" type="success" size="small">已生成</el-tag>
          <el-tag v-else type="info" size="small">未生成</el-tag>
        </el-form-item>
        <el-form-item label="任务配置：">
          <el-input
            type="textarea"
            :rows="12"
            readonly
            v-model="pluginConfigText"
            :placeholder="pluginHasToken ? '' : '尚未生成上传令牌，请先生成令牌再复制配置'"
          ></el-input>
        </el-form-item>
      </el-form>
      <div class="plugin-tip">将任务配置发送给负责该登录态的员工，在浏览器插件中导入即可；重置或吊销令牌后，员工手中的旧配置会立即失效，需重新分发。</div>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="!pluginHasToken" type="primary" :loading="tokenBusy" @click="doGenToken(false)">生成上传令牌</el-button>
        <el-button v-if="pluginHasToken" type="warning" :loading="tokenBusy" @click="doGenToken(true)">重置令牌</el-button>
        <el-button v-if="pluginHasToken" type="danger" plain :loading="tokenBusy" @click="doRevokeToken">吊销令牌</el-button>
        <el-button type="primary" plain :disabled="!pluginConfigText" @click="copyPluginConfig">复制配置</el-button>
        <el-button @click="pluginVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'FinanceLoginSession',
  data () {
    return {
      // 列表
      list: [],
      listLoading: false,
      // 添加/编辑弹窗
      dialogVisible: false,
      isEdit: false,
      editId: null,
      submitting: false,
      form: {
        platform: 'meituan',
        name: '',
        remark: '',
        contentText: '',
        pendingBrandId: '',
        pendingBrandName: ''
      },
      formRules: {
        platform: [{ required: true, message: '请选择平台', trigger: 'change' }],
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
      },
      // parse 即时解析结果（粘贴内容变化后回显）
      parseResult: { expireAt: null, daysLeft: null, error: '' },
      parseTimer: null,
      // 查看弹窗
      viewVisible: false,
      viewData: {},
      // 密钥管理
      secretForm: { secret: '' },
      secretLoaded: '',
      secretSaving: false,
      secretGenerating: false,
      // 插件配置弹窗
      pluginVisible: false,
      pluginRow: null,
      pluginBaseUrl: '',
      pluginHasToken: false,
      pluginConfigText: '',
      pluginLoading: false,
      tokenBusy: false
    }
  },
  computed: {
    contentPlaceholder () {
      if (this.form.platform === 'meituan') {
        return '粘贴 cookies 数组 JSON（scripts/export-pos-cookies.js 导出的文件内容），系统自动识别并包装'
      }
      if (this.form.platform === 'waimaie') {
        return '粘贴 cookies 数组 JSON（从已登录 waimaieapp.meituan.com 的浏览器导出，须含 token/acctId），系统自动识别并包装'
      }
      return '粘贴 JSON 对象：{"token":"eyJ...","brandId":"112281","brandName":"伊兴楼泡馍"}（dinghuotong-export-token.js 导出格式）'
    },
    parseDaysTagType () {
      if (this.parseResult.daysLeft === null || this.parseResult.daysLeft < 0) return 'danger'
      if (this.parseResult.daysLeft < 5) return 'warning'
      return 'success'
    },
    secretDirty () {
      return this.secretForm.secret !== this.secretLoaded
    },
    // 订货通 + 内容留空 → 显示待激活品牌字段（插件配置 consts 依赖 brandId）
    showPendingBrand () {
      return this.form.platform === 'dinghuotong' && !(this.form.contentText || '').trim()
    }
  },
  watch: {
    // 粘贴内容变化后防抖调后端 parse 接口即时回显过期时间（解析口径与后端保存一致）
    'form.contentText' (val) {
      clearTimeout(this.parseTimer)
      this.parseResult = { expireAt: null, daysLeft: null, error: '' }
      if (!val || !val.trim()) return
      this.parseTimer = setTimeout(() => this.doParse(), 800)
    }
  },
  mounted () {
    this.fetchList()
    this.fetchSecret()
  },
  methods: {
    platformLabel (platform) {
      const map = { meituan: '美团管家', dinghuotong: '订货通', waimaie: '美团外卖商家版' }
      return map[platform] || platform
    },
    platformTagType (platform) {
      const map = { meituan: 'warning', dinghuotong: 'primary', waimaie: 'success' }
      return map[platform] || 'primary'
    },
    // 剩余有效期 tag（与状态合并呈现：已过期=红 / 剩<5天=橙 / 正常=绿）
    // daysLeft 为向下取整：0 表示剩余不足 1 天（未过期），负数才是已过期
    expireTagType (row) {
      if (row.status === 'pending') return 'info'
      if (row.daysLeft === null || row.daysLeft === undefined || row.daysLeft < 0) return 'danger'
      if (row.daysLeft < 5) return 'warning'
      return 'success'
    },
    expireTagText (row) {
      if (row.status === 'pending') return '待激活'
      if (row.daysLeft === null || row.daysLeft === undefined || row.daysLeft < 0) return '已过期'
      if (row.daysLeft === 0) return '剩不足 1 天'
      return `剩 ${row.daysLeft} 天`
    },

    // ===== 列表 =====
    async fetchList () {
      this.listLoading = true
      try {
        const { data: res } = await this.$http.get('finance/loginSession/list')
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.list = res.data.list || []
      } catch (e) {
        this.$message.error('加载登录态列表失败：' + e.message)
      } finally {
        this.listLoading = false
      }
    },

    // ===== 添加/编辑 =====
    handleAdd () {
      this.isEdit = false
      this.editId = null
      this.dialogVisible = true
    },
    handleEdit (row) {
      this.isEdit = true
      this.editId = row._id
      this.form = {
        platform: row.platform,
        name: row.name,
        remark: row.remark || '',
        contentText: '',
        pendingBrandId: '',
        pendingBrandName: ''
      }
      this.dialogVisible = true
      // 编辑时回填当前内容（需拉详情获取 content）；
      // 带时效守卫：弹窗已关/已切为新增/已编辑其他行时丢弃响应，
      // 避免慢回填覆盖用户刚粘贴的内容或污染下一次新增表单
      const reqId = row._id
      this.$http.get(`finance/loginSession/detail/${reqId}`).then(({ data: res }) => {
        if (res.meta.status === 200 && this.dialogVisible && this.isEdit && this.editId === reqId) {
          this.form.contentText = this.formatContent(res.data.content)
          // 订货通待激活占位（有 brandId 无 token）→ 回填品牌字段
          const c = res.data.content
          if (c && c.brandId !== undefined && !c.token) {
            this.form.pendingBrandId = String(c.brandId)
            this.form.pendingBrandName = c.brandName || ''
          }
        }
      }).catch(() => {})
    },
    onPlatformChange () {
      // 平台切换后重新解析（占位符随计算属性变化）
      if (this.form.contentText) this.doParse()
    },
    // content 对象 → textarea 文本（美团回填 cookies 数组原文；订货通待激活占位不当作内容回填）
    formatContent (content) {
      if (!content) return ''
      if (content.brandId !== undefined && !content.token) return ''
      const obj = content.cookies ? content.cookies : content
      try {
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return ''
      }
    },
    // textarea 文本 → 提交用 content 对象（美团粘贴数组时自动包装为 { cookies: [...] }；内容留空=待激活建壳）
    buildContent () {
      const text = (this.form.contentText || '').trim()
      if (!text) {
        // 待激活建壳：美团/外卖空占位（content=null）；订货通必须携带 brandId（插件配置 consts 依赖）
        if (this.form.platform === 'dinghuotong') {
          const brandId = (this.form.pendingBrandId || '').trim()
          if (!brandId) return { error: '订货通待激活记录必须填写品牌ID（brandId）' }
          return { content: { brandId, brandName: (this.form.pendingBrandName || '').trim() } }
        }
        return { content: null }
      }
      let obj
      try {
        obj = JSON.parse(text)
      } catch (e) {
        return { error: '登录态内容不是合法的 JSON，请检查粘贴内容' }
      }
      if (this.form.platform === 'meituan' || this.form.platform === 'waimaie') {
        if (Array.isArray(obj)) return { content: { cookies: obj } }
        if (obj && typeof obj === 'object' && Array.isArray(obj.cookies)) return { content: obj }
        return { error: this.form.platform === 'waimaie' ? '美团外卖商家版登录态应为 cookies 数组 JSON（或含 cookies 数组的对象）' : '美团管家登录态应为 cookies 数组 JSON（或含 cookies 数组的对象）' }
      }
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return { error: '订货通登录态应为 JSON 对象（含 token、brandId）' }
      }
      return { content: obj }
    },
    async doParse () {
      const built = this.buildContent()
      if (built.error) {
        this.parseResult = { expireAt: null, daysLeft: null, error: built.error }
        return
      }
      try {
        const { data: res } = await this.$http.post('finance/loginSession/parse', {
          platform: this.form.platform,
          content: built.content
        })
        if (res.meta.status === 200) {
          this.parseResult = { expireAt: res.data.expireAt, daysLeft: res.data.daysLeft, error: '' }
        } else {
          this.parseResult = { expireAt: null, daysLeft: null, error: res.meta.msg || '解析失败' }
        }
      } catch (e) {
        this.parseResult = { expireAt: null, daysLeft: null, error: '解析请求失败：' + e.message }
      }
    },
    submitForm () {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        const built = this.buildContent()
        if (built.error) return this.$message.error(built.error)

        this.submitting = true
        try {
          const payload = {
            platform: this.form.platform,
            name: this.form.name.trim(),
            content: built.content,
            remark: this.form.remark || ''
          }
          const { data: res } = this.isEdit
            ? await this.$http.put(`finance/loginSession/modify/${this.editId}`, payload)
            : await this.$http.post('finance/loginSession/add', payload)

          if (res.meta.status === 200) {
            this.$message.success(res.meta.msg || '保存成功')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(res.meta.msg || '保存失败')
          }
        } catch (e) {
          this.$message.error('保存失败：' + e.message)
        } finally {
          this.submitting = false
        }
      })
    },
    resetForm () {
      this.$refs.formRef && this.$refs.formRef.resetFields()
      this.form = { platform: 'meituan', name: '', remark: '', contentText: '', pendingBrandId: '', pendingBrandName: '' }
      this.parseResult = { expireAt: null, daysLeft: null, error: '' }
      clearTimeout(this.parseTimer)
    },

    // ===== 查看/删除 =====
    async handleView (row) {
      try {
        const { data: res } = await this.$http.get(`finance/loginSession/detail/${row._id}`)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.viewData = {
          ...res.data,
          contentText: this.formatContent(res.data.content)
        }
        this.viewVisible = true
      } catch (e) {
        this.$message.error('获取详情失败：' + e.message)
      }
    },
    handleDelete (row) {
      this.$confirm(`确定删除登录态「${this.platformLabel(row.platform)} / ${row.name}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data: res } = await this.$http.delete(`finance/loginSession/remove/${row._id}`)
          if (res.meta.status === 200) {
            this.$message.success('删除成功')
            this.fetchList()
          } else {
            this.$message.error(res.meta.msg || '删除失败')
          }
        } catch (e) {
          this.$message.error('删除失败：' + e.message)
        }
      }).catch(() => {})
    },

    // ===== 密钥管理 =====
    async fetchSecret () {
      try {
        const { data: res } = await this.$http.get('finance/loginSession/secret')
        if (res.meta.status !== 200) return
        this.secretForm.secret = res.data.secret || ''
        this.secretLoaded = this.secretForm.secret
      } catch (e) {
        this.$message.error('获取密钥失败：' + e.message)
      }
    },
    // manual=true 手动保存输入值；manual=false 一键生成（不传 secret，后端自动生成）
    async saveSecret (manual) {
      if (manual) {
        const s = (this.secretForm.secret || '').trim()
        if (s.length < 16) return this.$message.error('密钥长度至少 16 个字符')
        if (s.length > 128) return this.$message.error('密钥长度不能超过 128 个字符')
      }
      manual ? (this.secretSaving = true) : (this.secretGenerating = true)
      try {
        const body = manual ? { secret: this.secretForm.secret.trim() } : {}
        const { data: res } = await this.$http.post('finance/loginSession/setSecret', body)
        if (res.meta.status === 200) {
          this.secretForm.secret = res.data.secret
          this.secretLoaded = res.data.secret
          this.$message.success(manual ? '密钥已保存' : '已生成新密钥（旧密钥已失效，请同步更新插件配置）')
        } else {
          this.$message.error(res.meta.msg || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败：' + e.message)
      } finally {
        this.secretSaving = false
        this.secretGenerating = false
      }
    },
    copySecret () {
      this.copyText(this.secretForm.secret)
    },
    copyText (text) {
      if (!text) return
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success('已复制到剪贴板')
      } catch (e) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(textarea)
    },

    // ===== 插件配置 =====
    handlePluginConfig (row) {
      this.pluginRow = row
      this.pluginBaseUrl = window.location.origin
      this.pluginHasToken = false
      this.pluginConfigText = ''
      this.pluginVisible = true
      this.fetchPluginConfig()
    },
    async fetchPluginConfig () {
      if (!this.pluginRow) return
      const base = (this.pluginBaseUrl || '').trim().replace(/\/+$/, '')
      this.pluginLoading = true
      try {
        const { data: res } = await this.$http.get(`finance/loginSession/pluginConfig/${this.pluginRow._id}`, { params: { baseUrl: base } })
        if (res.meta.status === 200) {
          this.pluginHasToken = true
          this.pluginConfigText = JSON.stringify(res.data.config, null, 2)
        } else if (/尚未生成/.test(res.meta.msg || '')) {
          this.pluginHasToken = false
          this.pluginConfigText = ''
        } else {
          this.$message.error(res.meta.msg || '获取配置失败')
        }
      } catch (e) {
        this.$message.error('获取配置失败：' + e.message)
      } finally {
        this.pluginLoading = false
      }
    },
    // reset=true 为重置（旧令牌立即失效，需二次确认并重新分发）
    doGenToken (reset) {
      const doGen = async () => {
        this.tokenBusy = true
        try {
          const { data: res } = await this.$http.post(`finance/loginSession/genToken/${this.pluginRow._id}`)
          if (res.meta.status === 200) {
            this.$message.success(reset ? '已重置令牌（旧令牌立即失效，请重新复制配置分发）' : '令牌已生成')
            this.fetchPluginConfig()
          } else {
            this.$message.error(res.meta.msg || '生成失败')
          }
        } catch (e) {
          this.$message.error('生成失败：' + e.message)
        } finally {
          this.tokenBusy = false
        }
      }
      if (!reset) return doGen()
      this.$confirm('重置后员工手中的旧配置会立即失效，需要重新分发，确定重置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(doGen).catch(() => {})
    },
    doRevokeToken () {
      this.$confirm('吊销后该登录态的插件上传会立即失效，确定吊销吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.tokenBusy = true
        try {
          const { data: res } = await this.$http.post(`finance/loginSession/revokeToken/${this.pluginRow._id}`)
          if (res.meta.status === 200) {
            this.$message.success('令牌已吊销')
            this.pluginHasToken = false
            this.pluginConfigText = ''
          } else {
            this.$message.error(res.meta.msg || '吊销失败')
          }
        } catch (e) {
          this.$message.error('吊销失败：' + e.message)
        } finally {
          this.tokenBusy = false
        }
      }).catch(() => {})
    },
    copyPluginConfig () {
      this.copyText(this.pluginConfigText)
    }
  }
}
</script>

<style scoped>
.search-card {
  margin-top: 20px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.toolbar-tip {
  color: #909399;
  font-size: 12px;
}
.card-header {
  font-size: 16px;
  font-weight: bold;
}
.sub-text {
  color: #909399;
  font-size: 12px;
}
.parse-error {
  color: #F56C6C;
  font-size: 13px;
}
.parse-ok {
  color: #67C23A;
  font-size: 13px;
}
.secret-row {
  display: flex;
  align-items: center;
}
.secret-tip {
  color: #909399;
  font-size: 12px;
  margin-top: -8px;
  margin-left: 120px;
}
.plugin-url-row {
  display: flex;
  align-items: center;
}
.plugin-tip {
  color: #909399;
  font-size: 12px;
  margin: 4px 0 8px;
}
</style>
